import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle, faSearch, faBan } from '@fortawesome/free-solid-svg-icons';
import { Card, Form, Button, Alert, Badge, Col, Row } from '@themesberg/react-bootstrap';
import { collection, query, onSnapshot, doc, deleteDoc, updateDoc, orderBy, collectionGroup, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

// Türkçe küfür/hakaret listesi
const BAD_WORDS = [
    'amk', 'aq', 'amına', 'orospu', 'piç', 'sik', 'yarak', 'yarrak', 'göt',
    'pezevenk', 'kahpe', 'ibne', 'gerizekalı', 'salak', 'aptal', 'mal',
    'dangalak', 'hıyar', 'sürtük', 'kaltak', 'şerefsiz', 'namussuz',
    'puşt', 'gavat', 'döl', 'meme', 'am ', 'taşak', 'siktir', 'fuck',
    'shit', 'bitch', 'ass', 'dick', 'pussy', 'whore', 'slut',
    'bok', 'osur', 'coduk', 'anan', 'babanı', 'ananı', 'bacını'
];

// Telefon numarası pattern (0-9 arası rakamlar içeriyor mu)
const hasPhoneNumber = (text) => {
    if (!text) return false;
    // 5+ ardışık rakam veya rakamlar arasında boşluk/tire ile
    const digitOnly = text.replace(/[\s\-\(\)\.]/g, '');
    const digitMatches = digitOnly.match(/\d{5,}/);
    if (digitMatches) return true;
    // 0 ile başlayan numara pattern'i
    if (/0\s*\d[\s\-]?\d[\s\-]?\d/.test(text)) return true;
    return false;
};

// Küfür/hakaret kontrolü
const hasProfanity = (text) => {
    if (!text) return false;
    const lower = text.toLowerCase().replace(/[ıİ]/g, 'i').replace(/[şŞ]/g, 's').replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g').replace(/[üÜ]/g, 'u').replace(/[öÖ]/g, 'o');
    return BAD_WORDS.some(word => lower.includes(word.toLowerCase()));
};

const getFlagReasons = (text) => {
    const reasons = [];
    if (hasProfanity(text)) reasons.push('Küfür/Hakaret');
    if (hasPhoneNumber(text)) reasons.push('Telefon Numarası');
    return reasons;
};

export default () => {
    const [allComments, setAllComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [censoringId, setCensoringId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Tüm yorumları collectionGroup ile çek
    useEffect(() => {
        const loadComments = async () => {
            try {
                setLoading(true);

                // collectionGroup ile tüm comments subcollection'larını çek
                const commentsSnapshot = await getDocs(
                    query(collectionGroup(db, 'comments'), orderBy('createdAt', 'desc'))
                );

                const commentsList = [];

                for (const commentDoc of commentsSnapshot.docs) {
                    const data = commentDoc.data();
                    const parentRef = commentDoc.ref.parent.parent; // content/{postId}

                    if (!parentRef) continue;

                    // Parent post bilgilerini çek
                    let postData = null;
                    try {
                        const postSnap = await getDoc(parentRef);
                        if (postSnap.exists()) {
                            postData = postSnap.data();
                        }
                    } catch (e) {
                        // Post silinmiş olabilir
                    }

                    const text = data.text || '';
                    const flagReasons = getFlagReasons(text);

                    commentsList.push({
                        id: commentDoc.id,
                        postId: parentRef.id,
                        text: text,
                        authorId: data.authorId || '',
                        authorName: data.authorName || 'İsimsiz',
                        authorAvatar: data.authorAvatar || null,
                        authorEmail: data.authorEmail || null,
                        createdAt: data.createdAt?.toDate?.() || null,
                        // Post bilgileri
                        postAuthorName: postData?.authorName || 'Bilinmeyen',
                        postAuthorAvatar: postData?.authorAvatar || null,
                        postImageUrl: postData?.imageUrl || null,
                        postText: postData?.text || null,
                        // Flag
                        isFlagged: flagReasons.length > 0,
                        flagReasons: flagReasons
                    });
                }

                // Flagged yorumları en başa koy
                commentsList.sort((a, b) => {
                    if (a.isFlagged && !b.isFlagged) return -1;
                    if (!a.isFlagged && b.isFlagged) return 1;
                    // Aynı flag durumundakileri tarihe göre sırala
                    const dateA = a.createdAt?.getTime() || 0;
                    const dateB = b.createdAt?.getTime() || 0;
                    return dateB - dateA;
                });

                setAllComments(commentsList);
                setLoading(false);
            } catch (err) {
                console.error("Error loading comments:", err);
                setError("Yorumlar yüklenirken hata oluştu: " + err.message);
                setLoading(false);
            }
        };

        loadComments();
    }, []);

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleString('tr-TR', {
            timeZone: 'Europe/Istanbul',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const handleCensorComment = async (comment) => {
        setCensoringId(comment.id);
        try {
            const censoredText = '**** (Bu yorum kurallara aykırı olduğu için sansürlendi)';
            await updateDoc(doc(db, 'content', comment.postId, 'comments', comment.id), { text: censoredText });
            setAllComments(prev => prev.map(c => c.id === comment.id ? { ...c, text: censoredText, isFlagged: false } : c));
        } catch (err) {
            setError('Sansürleme hatası: ' + err.message);
        } finally {
            setCensoringId(null);
        }
    };

    const handleDeleteComment = async (comment) => {
        if (!window.confirm("Bu yorumu kalıcı olarak silmek istediğinizden emin misiniz?")) return;
        setDeletingId(comment.id);
        try {
            await deleteDoc(doc(db, 'content', comment.postId, 'comments', comment.id));
            // Yorum sayısını güncelle
            try {
                const postRef = doc(db, 'content', comment.postId);
                const postSnap = await getDoc(postRef);
                if (postSnap.exists()) {
                    const currentCount = postSnap.data().commentCount || 0;
                    await updateDoc(postRef, { commentCount: Math.max(0, currentCount - 1) });
                }
            } catch (e) {
                // Post silinmiş olabilir
            }
            setAllComments(prev => prev.filter(c => c.id !== comment.id));
            setSuccess("Yorum silindi.");
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError("Yorum silme hatası: " + err.message);
        } finally {
            setDeletingId(null);
        }
    };

    // Filtreleme
    const filteredComments = React.useMemo(() => {
        if (!searchTerm) return allComments;
        const lower = searchTerm.toLowerCase();
        return allComments.filter(c =>
            (c.authorName?.toLowerCase() || '').includes(lower) ||
            (c.text?.toLowerCase() || '').includes(lower) ||
            (c.postAuthorName?.toLowerCase() || '').includes(lower)
        );
    }, [allComments, searchTerm]);

    const flaggedCount = allComments.filter(c => c.isFlagged).length;

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h4>Yorumlar</h4>
                    <p className="mb-0 text-muted">
                        Tüm gönderilerdeki yorumları yönetin.
                        {!loading && (
                            <span className="ms-2">
                                <strong>{allComments.length}</strong> yorum
                                {flaggedCount > 0 && (
                                    <Badge bg="danger" className="ms-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                        {flaggedCount} uyarılı
                                    </Badge>
                                )}
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>{success}</Alert>}

            <Card border="light" className="shadow-sm">
                <Card.Body className="pt-0">
                    <div className="table-settings mb-4">
                        <Row className="align-items-center justify-content-between">
                            <Col xs={12} md={6} className="mt-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <FontAwesomeIcon icon={faSearch} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Yazar, yorum veya gönderi sahibi ara..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Yükleniyor...</span>
                            </div>
                            <p className="text-muted mt-3">Tüm yorumlar yükleniyor, bu biraz zaman alabilir...</p>
                        </div>
                    ) : filteredComments.length === 0 ? (
                        <div className="text-center py-5 text-muted">Henüz yorum yok.</div>
                    ) : (
                        <div>
                            {filteredComments.map((comment) => (
                                <div
                                    key={comment.id}
                                    className="d-flex align-items-start p-3 mb-2"
                                    style={{
                                        background: comment.isFlagged ? '#2a1a1a' : '#16213e',
                                        borderRadius: '8px',
                                        borderLeft: '4px solid',
                                        borderLeftColor: comment.isFlagged ? '#dc3545' : '#6c757d',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {/* Gönderi Görseli */}
                                    <div className="me-3" style={{ minWidth: '60px' }}>
                                        {comment.postImageUrl ? (
                                            <img
                                                src={comment.postImageUrl}
                                                alt="Gönderi"
                                                style={{ width: 55, height: 55, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                                                onClick={() => window.open(comment.postImageUrl, '_blank')}
                                            />
                                        ) : (
                                            <div style={{ width: 55, height: 55, borderRadius: 8, background: '#0d1b2a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ color: '#6c757d', fontSize: '0.7rem' }}>Görsel yok</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* İçerik */}
                                    <div className="flex-grow-1">
                                        {/* Gönderi sahibi */}
                                        <div className="d-flex align-items-center mb-1">
                                            <small style={{ color: '#8e9aaf' }}>
                                                Gönderi: <strong style={{ color: '#a0b4d0' }}>{comment.postAuthorName}</strong>
                                            </small>
                                            {comment.postText && (
                                                <small style={{ color: '#6c757d', marginLeft: '8px' }}>
                                                    "{comment.postText.length > 30 ? comment.postText.substring(0, 30) + '...' : comment.postText}"
                                                </small>
                                            )}
                                        </div>

                                        {/* Yorum metni */}
                                        <div style={{
                                            color: comment.isFlagged ? '#ff8a8a' : '#e0e6ed',
                                            fontSize: '0.95rem',
                                            marginBottom: '4px',
                                            wordWrap: 'break-word'
                                        }}>
                                            {comment.text}
                                        </div>

                                        {/* Yorum yapan */}
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            {comment.authorAvatar ? (
                                                <img src={comment.authorAvatar} alt="" className="rounded-circle" style={{ width: 22, height: 22, objectFit: 'cover' }} />
                                            ) : (
                                                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 22, height: 22 }}>
                                                    <span style={{ fontSize: '0.6rem', color: '#fff' }}>{comment.authorName?.charAt(0)?.toUpperCase() || '?'}</span>
                                                </div>
                                            )}
                                            <small style={{ color: '#8e9aaf' }}>
                                                <strong style={{ color: '#a0b4d0' }}>{comment.authorName}</strong>
                                                {comment.authorEmail && <span style={{ color: '#6c757d' }} className="ms-1">({comment.authorEmail})</span>}
                                            </small>
                                            <small style={{ color: '#6c757d' }}>{formatDate(comment.createdAt)}</small>
                                        </div>

                                        {/* Flag uyarıları */}
                                        {comment.isFlagged && (
                                            <div className="mt-2">
                                                {comment.flagReasons.map((reason, idx) => (
                                                    <Badge key={idx} bg="danger" className="me-1">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                        {reason}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Sil ve Sansürle butonları */}
                                    <div className="ms-3 d-flex flex-column gap-1">
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            onClick={() => handleCensorComment(comment)}
                                            disabled={censoringId === comment.id}
                                            title="Sansürle"
                                        >
                                            {censoringId === comment.id ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FontAwesomeIcon icon={faBan} />
                                            )}
                                        </Button>
                                        <Button
                                            variant={comment.isFlagged ? "danger" : "outline-danger"}
                                            size="sm"
                                            onClick={() => handleDeleteComment(comment)}
                                            disabled={deletingId === comment.id}
                                            title="Yorumu Sil"
                                        >
                                            {deletingId === comment.id ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FontAwesomeIcon icon={faTrash} />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};
